'use client';

import { useEffect, useRef } from 'react';
import { buildUrlManager, UrlManager, UrlManagerState } from '@coveo/headless';
import { useCoveoEngine } from 'lib/utils/coveo/SearchContext/CoveoEngineContext';
import { CoveoHistoryManager } from 'helpers/Coveo/coveo-history-manager';
import { useRouter } from 'next/router';

const UrlManagerController = ({
  preserveSearchQuery = false,
}: {
  preserveSearchQuery?: boolean;
}) => {
  const engine = useCoveoEngine();
  const urlManagerRef = useRef<UrlManager | null>(null);
  const router = useRouter();

  useEffect(() => {
    if (!engine || !router.isReady) return;

    let initialFragment = '';

    if (typeof window !== 'undefined') {
      const url = new URL(window.location.href);
      const searchParams = url.searchParams;

      // Extract only Coveo parameters from existing hash (ignoring HTML anchors like #reviews)
      const existingHash = url.hash.startsWith('#') ? url.hash.slice(1) : url.hash;
      const hashParams = new URLSearchParams();

      if (existingHash) {
        const rawHashParams = new URLSearchParams(existingHash);
        rawHashParams.forEach((value, key) => {
          const cleanKey = key.startsWith('#') ? key.substring(1) : key;
          if (
            cleanKey === 'q' ||
            cleanKey.startsWith('f-') ||
            cleanKey === 'sortCriteria' ||
            cleanKey === 'firstResult'
          ) {
            hashParams.set(cleanKey, value);
          }
        });
      }

      // Move Coveo query parameters from ?searchParams to #hashParams
      const keysToDelete: string[] = [];
      searchParams.forEach((value, key) => {
        const cleanKey = key.startsWith('#') ? key.substring(1) : key;
        if (
          cleanKey === 'q' ||
          cleanKey.startsWith('f-') ||
          cleanKey === 'sortCriteria' ||
          cleanKey === 'firstResult'
        ) {
          hashParams.set(cleanKey, value);
          if (!preserveSearchQuery || cleanKey !== 'q') {
            keysToDelete.push(key);
          }
        }
      });
      keysToDelete.forEach((key) => searchParams.delete(key));

      initialFragment = hashParams.toString().replace(/\+/g, '%20');
      url.hash = initialFragment ? `#${initialFragment}` : '';

      // Clean the URL using replaceState if Sitecore query parameters were migrated
      if (keysToDelete.length > 0) {
        router.replace(`${url.pathname}${url.search}${url.hash}`, undefined, { shallow: true });
      }
    }

    const urlManager = buildUrlManager(engine, {
      initialState: { fragment: initialFragment },
    });
    urlManagerRef.current = urlManager;

    // Subscribe to state changes from Coveo
    const unsubscribe = urlManager.subscribe(() => {
      if (typeof window !== 'undefined') {
        const state: UrlManagerState = urlManager.state;
        const url = new URL(window.location.href);

        url.searchParams.delete('path');

        const keysToDelete: string[] = [];
        url.searchParams.forEach((_, key) => {
          const cleanKey = key.startsWith('#') ? key.substring(1) : key;
          if (
            (cleanKey === 'q' && !preserveSearchQuery) ||
            cleanKey.startsWith('f-') ||
            cleanKey === 'sortCriteria' ||
            cleanKey === 'firstResult'
          ) {
            keysToDelete.push(key);
          }
        });
        keysToDelete.forEach((key) => url.searchParams.delete(key));

        const stateParams = new URLSearchParams(state.fragment);
        if (stateParams.has('cq')) {
          stateParams.delete('cq');
        }

        if (preserveSearchQuery) {
          const searchQuery = stateParams.get('q');
          if (searchQuery === null || searchQuery === '') {
            url.searchParams.delete('q');
            stateParams.delete('q');
          } else {
            url.searchParams.set('q', searchQuery);
          }
        }

        const cleanedFragment = stateParams.toString().replace(/\+/g, '%20');
        url.hash = cleanedFragment ? `#${cleanedFragment}` : '';

        if (window.location.href !== url.toString()) {
          if (CoveoHistoryManager.consume()) {
            window.history.replaceState(null, '', url.toString());
          } else {
            window.history.pushState(null, '', url.toString());
          }
        }
      }
    });

    const onHashChange = () => {
      if (typeof window !== 'undefined') {
        const currentFragment = window.location.hash.slice(1);
        urlManager.synchronize(currentFragment);
      }
    };

    window.addEventListener('hashchange', onHashChange);

    const timer = setTimeout(() => {
      engine.executeFirstSearch();
    }, 0);

    return () => {
      clearTimeout(timer);
      unsubscribe();
      window.removeEventListener('hashchange', onHashChange);
    };
  }, [engine, preserveSearchQuery, router]);

  return null;
};

export default UrlManagerController;
